"use client"

import * as React from "react"
import { format } from "date-fns"

import { useChurchNow } from "@/shared/hooks/use-church-now"
import { PeriodPreset, resolvePeriodRange } from "@/shared/lib/dates/period-range"
import type { ResolvedDashboardDateRange } from "@/features/dashboard/lib/resolve-dashboard-date-range"

/** Month-to-date matches the dashboard's long-standing default ("this month through today"). */
const DEFAULT_PRESET = PeriodPreset.MonthToDate

export type UseDashboardDateRangeResult = {
  preset: PeriodPreset
  setPreset: (preset: PeriodPreset) => void
  /** Range start, `YYYY-MM-DD` in the church's timezone. */
  from: string
  /** Range end, `YYYY-MM-DD` in the church's timezone. */
  to: string
  /** The `{ dateFrom, dateTo }` shape the overview data layer consumes. */
  resolvedRange: ResolvedDashboardDateRange
}

/**
 * The dashboard overview's reporting period. Owns the preset selection and resolves it — in the church's
 * timezone (IBA-118), via the same {@link resolvePeriodRange} engine Finance Overview uses — to date-only
 * bounds. The resolved range fans out to every overview query, so changing the preset re-fetches the
 * dashboard for the new window.
 */
export function useDashboardDateRange(): UseDashboardDateRangeResult {
  // The church's clock, not the browser's — "this month" is the church's month (IBA-118).
  const churchNow = useChurchNow()
  const [preset, setPreset] = React.useState<PeriodPreset>(DEFAULT_PRESET)

  const { from, to } = React.useMemo(() => {
    // The dashboard picker only offers calendar presets (no fiscal ones), so a resolve always succeeds; the
    // fallback exists purely so an unexpected null can never leave the range undefined.
    const range =
      resolvePeriodRange(preset, { now: churchNow, weekStartsOn: 1 }) ??
      resolvePeriodRange(DEFAULT_PRESET, { now: churchNow, weekStartsOn: 1 })!
    return { from: format(range.start, "yyyy-MM-dd"), to: format(range.end, "yyyy-MM-dd") }
  }, [preset, churchNow])

  const resolvedRange = React.useMemo<ResolvedDashboardDateRange>(
    () => ({ dateFrom: from, dateTo: to }),
    [from, to],
  )

  return { preset, setPreset, from, to, resolvedRange }
}
